// The export jobs of M7 slice 7 (spec/api-conventions.md section 21.7): `analytics.export` and `players.export` write gzip
// NDJSON that `GET …/jobs/{job}/download` streams with the caller's credential. ExportJob follows the queued job with
// JobProgress and, once it succeeded, shows the result (rows, size, expiry) and the download link — a link whose click
// fetches the file with the bearer (a plain link cannot carry it) and hands the browser the blob to save.
import { useState, type MouseEvent } from 'react';
import { ApiError } from '../api/client';
import { ops } from '../api/operations';
import type { Job } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { useGame } from '../shell/GameContext';
import { JobProgress } from '../ui/JobProgress';
import { formatInstantWithZone, useTimeZone } from '../ui/time';
import { useToasts } from '../ui/Toast';

/** Hands the browser a Blob to save (a Blob URL on a temporary link). */
export function saveBlob(filename: string, blob: Blob): void {
  try {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  } catch {
    // no Blob URLs (an old browser, a test without them): nothing to save
  }
}

export interface ExportResult {
  download?: string;
  bytes?: number | string;
  rows?: number | string;
  expiresAt?: string | null;
  expired?: boolean;
}

function bytesText(value: number | string | undefined): string {
  const v = Number(value ?? 0);
  if (v < 1024) return `${v} B`;
  if (v < 1048576) return `${(v / 1024).toFixed(1)} KB`;
  return `${(v / 1048576).toFixed(1)} MB`;
}

/** Follows an export job and offers its file. `filename` is the fallback name when the answer names none. */
export function ExportJob({ job, filename, permission }: { job: Job; filename: string; permission: string | null }) {
  const { client } = useAuth();
  const { org, game, offsets } = useGame();
  const [zone] = useTimeZone();
  const toasts = useToasts();
  const [finished, setFinished] = useState<Job | null>(null);
  const [current, setCurrent] = useState<Job>(job);
  const [downloading, setDownloading] = useState(false);
  const shown = finished && finished.id === current.id ? finished : null;
  const result = (shown?.status === 'succeeded' ? (shown.result as ExportResult | null) : null) ?? null;
  const href = client.url(ops.downloadJobResult, { org, game: game.slug, job: current.id });
  const download = async (e: MouseEvent) => {
    e.preventDefault();
    if (downloading) return;
    setDownloading(true);
    try {
      const r = await client.blob(ops.downloadJobResult, { path: { org, game: game.slug, job: current.id } });
      const name = r.filename ?? filename;
      saveBlob(name, r.blob);
      toasts.success(`Downloaded ${name}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) toasts.error('The export is gone: its file is kept 7 days. Export again.', { requestId: err.requestId });
      else toasts.error(err instanceof Error ? err.message : String(err), err instanceof ApiError ? { requestId: err.requestId } : undefined);
    } finally {
      setDownloading(false);
    }
  };
  return (
    <div className="stack export-job" data-testid="export-job">
      <JobProgress
        job={current}
        api={{ client, org, game: game.slug, missingPermission: permission }}
        onDone={(j) => setFinished(j)}
        onRetry={(j) => {
          setFinished(null);
          setCurrent(j);
        }}
      />
      {result &&
        (result.expired ? (
          <p className="muted small" data-testid="export-expired">
            The file expired (kept 7 days). Export again.
          </p>
        ) : (
          <p className="row wrap export-result" data-testid="export-result">
            <a href={href} onClick={(e) => void download(e)} className="btn btn-s btn-primary" aria-busy={downloading} download={filename}>
              Download {filename}
            </a>
            <span className="muted small">
              {String(result.rows ?? 0)} row{Number(result.rows ?? 0) === 1 ? '' : 's'} · {bytesText(result.bytes)} gzip NDJSON
              {result.expiresAt ? ` · kept until ${formatInstantWithZone(result.expiresAt, zone, { offsets })}` : ''}
            </span>
          </p>
        ))}
    </div>
  );
}
